import React from "react";
import { Outlet, NavLink, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard,
  FileText,
  Users,
  Briefcase,
  Trash2,
  LogOut,
  Leaf,
  Clock,
  Scale,
  History,
  Sparkles,
} from "lucide-react";

const nav = [
  { to: "/", icon: LayoutDashboard, label: "Dashboard", end: true, testid: "nav-dashboard" },
  { to: "/bills", icon: FileText, label: "Bills", testid: "nav-bills" },
  { to: "/partial-payments", icon: Clock, label: "With Employee", testid: "nav-partial" },
  { to: "/employees", icon: Briefcase, label: "Employees", testid: "nav-employees" },
  { to: "/buyers", icon: Users, label: "Buyers", testid: "nav-buyers" },
  { to: "/recycle-bin", icon: Trash2, label: "Recycle Bin", testid: "nav-recycle-bin" },
];

const ownerNav = [
  { to: "/assistant", icon: Sparkles, label: "Assistant", testid: "nav-assistant" },
  { to: "/reconciliation", icon: Scale, label: "Reconciliation", testid: "nav-reconciliation" },
  { to: "/audit-log", icon: History, label: "Audit Log", testid: "nav-audit-log" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="App flex min-h-screen">
      <aside className="w-64 shrink-0 border-r border-neutral-200 bg-white flex flex-col">
        <div className="px-6 py-6 border-b border-neutral-200">
          <Link
            to="/"
            data-testid="brand-home-link"
            className="flex items-center gap-2 group focus:outline-none focus-visible:ring-2 focus-visible:ring-forest-600/40 rounded-lg"
          >
            <div className="w-8 h-8 rounded-lg bg-forest-600 text-bone-100 flex items-center justify-center transition-transform group-hover:scale-105">
              <Leaf size={16} strokeWidth={1.75} />
            </div>
            <div className="font-display text-xl font-semibold tracking-tight group-hover:text-forest-600 transition-colors">
              Payments
            </div>
          </Link>
          <div className="text-[11px] uppercase tracking-widest text-ink-muted mt-2">
            Order Ledger
          </div>
        </div>
        <nav className="flex-1 py-4">
          {[...nav, ...(["admin", "owner"].includes(user?.role) ? ownerNav : [])].map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={n.testid}
              className={({ isActive }) =>
                `mx-3 mb-1 flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-forest-600 text-bone-100 shadow-sm"
                    : "text-ink-soft hover:bg-bone-200 hover:text-ink"
                }`
              }
            >
              <n.icon size={16} strokeWidth={1.75} />
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-neutral-200 p-3 space-y-1">
          <div className="px-3 pb-2">
            <div className="text-[11px] uppercase tracking-widest text-ink-muted">
              Signed in as
            </div>
            <div className="text-sm font-medium truncate" data-testid="current-user-username">
              {user?.username}
            </div>
          </div>
          <button
            onClick={handleLogout}
            data-testid="logout-btn"
            className="w-full flex items-center gap-2 text-sm px-3 py-2 rounded-lg hover:bg-terracotta-100 text-terracotta-600"
          >
            <LogOut size={14} strokeWidth={1.75} /> Sign out
          </button>
        </div>
      </aside>
      <main className="flex-1 min-w-0 overflow-x-hidden">
        <Outlet />
      </main>
    </div>
  );
}
